/**
 * aba-integracoes.tsx — a aba do Tiny, dentro de Configurações.
 *
 * Peça de UMA tela: kebab-case no diretório do módulo, sem `Custom`.
 *
 * ─── REDESENHADA EM 25/09/2026 (ficha #1) ────────────────────────────────────
 * Dele, olhando a versão anterior: *"Ele simplesmente acha que está conectado,
 * ele chuta, ou ele sabe? Cadê a v2? Estou sincronizando de qual loja?"*
 *   1. O "Conectado" chutava — queria dizer só "há token guardado". Agora cada
 *      API é PERGUNTADA ao abrir (`/conexoes/{loja}/teste`) e mostra QUAL
 *      EMPRESA respondeu. Empresa errada, ou CNPJs diferentes entre v3 e v2,
 *      deixam a loja em "Atenção".
 *   2. A v2 não aparecia, e a chave dela só se trocava no servidor. Agora tem
 *      linha própria e "Trocar chave" (migration 111).
 *   3. O Sincronizar puxava as duas lojas sem dizer. Agora é por loja.
 *
 * ─── O MOLDE ─────────────────────────────────────────────────────────────────
 * A primeira versão foi feita sem abrir o tema, e ele pegou na hora: *"olha
 * como está confuso, desarranjado — olha o tamanho do botão testar"*. O molde é
 * a lista de redes de `adminux-network-dashboard.html`: TABELA, com ícone
 * colorido em `avatar-40 rounded`, nome e linha cinza de apoio, a situação numa
 * coluna e as ações noutra. A tabela é o que alinha; todos os botões `btn-sm`.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { formatarDataHora } from '@/lib/data';

interface TinyStatus {
  loja_id: number;
  loja_codigo: string;
  loja_nome: string;
  loja_nome_curto: string;
  conectado: boolean;
}

interface CategoriaResumo {
  loja_codigo: string;
  qtd: number;
  ultima_sincronizacao?: string | null;
}

interface TesteApi {
  ok: boolean;
  empresa?: string | null;
  cnpj?: string | null;
  origem?: 'tela' | 'servidor' | null;
  erro?: string;
}

interface Teste {
  loja: string;
  testado_em: string;
  v3: TesteApi;
  v2: TesteApi;
  confere: boolean;
  avisos: string[];
}

const BASE = '/configuracoes/integracoes/tiny';

/** A primeira coluna: ícone colorido + nome + para que serve (molde do tema). */
function Identidade({ icone, cor, nome, uso }: { icone: string; cor: string; nome: string; uso: string }) {
  return (
    <div className="row gx-3 align-items-center flex-nowrap">
      <div className="col-auto">
        <i className={`bi ${icone} avatar avatar-40 bg-theme-1-subtle text-theme-1 rounded ${cor}`}></i>
      </div>
      <div className="col">
        <p className="mb-0 fw-medium">{nome}</p>
        <p className="text-secondary small mb-0">{uso}</p>
      </div>
    </div>
  );
}

/** A coluna de situação: selo + quem respondeu (ou o motivo). */
function Situacao({ t, carregando, extra }: { t?: TesteApi; carregando: boolean; extra?: ReactNode }) {
  if (carregando || !t) {
    return (
      <span className="text-secondary small">
        <span className="spinner-border spinner-border-sm me-2" />
        Perguntando ao Tiny…
      </span>
    );
  }
  if (!t.ok) {
    return (
      <>
        <span className="badge bg-danger-subtle text-danger-emphasis">Sem resposta</span>
        <p className="text-secondary small mb-0 mt-1">{t.erro}</p>
      </>
    );
  }
  return (
    <>
      <span className="badge bg-success-subtle text-success-emphasis">Respondendo</span>
      <p className="small mb-0 mt-1">
        {t.empresa}
        {t.cnpj && <span className="text-secondary"> · {t.cnpj}</span>}
      </p>
      {extra}
    </>
  );
}

export function AbaIntegracoes() {
  const [sp, setSp] = useSearchParams();
  const [lojas, setLojas] = useState<TinyStatus[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [catResumo, setCatResumo] = useState<CategoriaResumo[]>([]);
  const [testes, setTestes] = useState<Record<string, Teste>>({});
  const [testando, setTestando] = useState<Record<string, boolean>>({});
  const [sincronizando, setSincronizando] = useState<string | null>(null);
  // A loja cuja chave da v2 está sendo trocada, e o que foi colado.
  const [trocandoChave, setTrocandoChave] = useState<string | null>(null);
  const [chave, setChave] = useState('');
  const [salvandoChave, setSalvandoChave] = useState(false);
  // Desconectar pede confirmação: apaga a chave e para o envio de despesa da loja.
  const [desconectando, setDesconectando] = useState<TinyStatus | null>(null);

  async function testar(loja: string) {
    setTestando((m) => ({ ...m, [loja]: true }));
    try {
      const t = await api.get<Teste>(`${BASE}/conexoes/${loja}/teste`);
      setTestes((m) => ({ ...m, [loja]: t }));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível testar.');
    } finally {
      setTestando((m) => ({ ...m, [loja]: false }));
    }
  }

  async function carregar() {
    setCarregando(true);
    try {
      const ls = await api.get<TinyStatus[]>(`${BASE}/status`);
      setLojas(ls);
      ls.forEach((l) => testar(l.loja_codigo));
    } catch {
      /* silencioso */
    } finally {
      setCarregando(false);
    }
  }

  async function carregarCategorias() {
    try {
      setCatResumo(await api.get<CategoriaResumo[]>(`${BASE}/categorias/resumo`));
    } catch {
      /* silencioso */
    }
  }

  useEffect(() => {
    carregar();
    carregarCategorias();
    const t = sp.get('tiny');
    if (t === 'ok') toast.success(`Loja ${sp.get('loja') ?? ''} conectada ao Tiny.`);
    else if (t === 'erro') toast.error('Não foi possível conectar ao Tiny.');
    if (t) {
      sp.delete('tiny');
      sp.delete('loja');
      setSp(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function conectar(loja: string) {
    window.location.href = `/api${BASE}/conexoes/autorizar?loja=${loja}`;
  }

  async function copiarLink(l: TinyStatus) {
    try {
      const { url } = await api.get<{ url: string }>(`${BASE}/conexoes/${l.loja_codigo}/link`);
      await navigator.clipboard.writeText(url);
      toast.success(`Link copiado. Cole no navegador onde o Tiny de ${l.loja_nome_curto} está logado.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível copiar o link.');
    }
  }

  async function desconectar(loja: string) {
    try {
      await api.delete(`${BASE}/conexoes/${loja}`);
      toast.success('Desconectado do Tiny.');
      carregar();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível desconectar.');
    } finally {
      setDesconectando(null);
    }
  }

  async function salvarChave(loja: string) {
    setSalvandoChave(true);
    try {
      const t = await api.put<Teste>(`${BASE}/conexoes/${loja}/chave-v2`, { token: chave });
      setTestes((m) => ({ ...m, [loja]: t }));
      setTrocandoChave(null);
      setChave('');
      toast.success('Chave da v2 conferida no Tiny e salva.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível salvar a chave.');
    } finally {
      setSalvandoChave(false);
    }
  }

  async function sincronizar(l: TinyStatus) {
    setSincronizando(l.loja_codigo);
    try {
      const [r] = await api.post<{ loja: string; total?: number; erro?: string }[]>(
        `${BASE}/categorias/sincronizar?loja=${l.loja_codigo}`,
      );
      if (r?.erro) toast.error(`${l.loja_nome_curto}: ${r.erro}`);
      else toast.success(`${l.loja_nome_curto}: ${r?.total ?? 0} categorias sincronizadas.`);
      carregarCategorias();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível sincronizar.');
    } finally {
      setSincronizando(null);
    }
  }

  if (carregando) {
    return (
      <div className="d-flex align-items-center gap-2 text-secondary py-3">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {lojas.map((l) => {
        const t = testes[l.loja_codigo];
        const emTeste = !!testando[l.loja_codigo];
        const cat = catResumo.find((c) => c.loja_codigo === l.loja_codigo);
        const selo = !t || emTeste
          ? null
          : t.confere
            ? { classe: 'bg-success-subtle text-success-emphasis', texto: 'Tudo certo' }
            : { classe: 'bg-danger-subtle text-danger-emphasis', texto: 'Atenção' };
        return (
          <div className="card adminuiux-card" key={l.loja_codigo}>
            <div className="card-header bg-transparent">
              <div className="row gx-3 align-items-center">
                <div className="col-auto">
                  <i className="bi bi-shop avatar avatar-40 bg-theme-1 text-white rounded"></i>
                </div>
                <div className="col">
                  <h6 className="mb-0">{l.loja_nome_curto}</h6>
                  <p className="text-secondary small mb-0">
                    {t && !emTeste
                      ? `Conta do Tiny · testada em ${formatarDataHora(t.testado_em)}`
                      : 'Conta do Tiny'}
                  </p>
                </div>
                {selo && (
                  <div className="col-auto">
                    <span className={`badge ${selo.classe}`}>{selo.texto}</span>
                  </div>
                )}
                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-theme"
                    onClick={() => testar(l.loja_codigo)}
                    disabled={emTeste}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>Testar
                  </button>
                </div>
              </div>
            </div>

            {t && !emTeste && t.avisos.length > 0 && (
              <div className="alert alert-danger small mx-3 mb-0">
                {t.avisos.map((a) => (
                  <div key={a}>{a}</div>
                ))}
              </div>
            )}

            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <colgroup>
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '36%' }} />
                  <col />
                </colgroup>
                <tbody>
                  <tr>
                    <td className="ps-3">
                      <Identidade
                        icone="bi-send"
                        cor="theme-blue"
                        nome="API v3"
                        uso="Lança a despesa e traz as categorias"
                      />
                    </td>
                    <td>
                      <Situacao t={t?.v3} carregando={emTeste} />
                    </td>
                    <td className="text-end text-nowrap pe-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-theme me-2"
                        onClick={() => copiarLink(l)}
                        title="Para colar no navegador onde o Tiny desta loja está logado"
                      >
                        <i className="bi bi-clipboard me-1"></i>Copiar link
                      </button>
                      {l.conectado ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setDesconectando(l)}
                        >
                          Desconectar
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-theme"
                          onClick={() => conectar(l.loja_codigo)}
                          title="Só serve se o Tiny desta loja estiver logado neste navegador"
                        >
                          Conectar aqui
                        </button>
                      )}
                    </td>
                  </tr>

                  <tr>
                    <td className="ps-3">
                      <Identidade
                        icone="bi-key"
                        cor="theme-orange"
                        nome="API v2"
                        uso="Puxa o pedido de venda para a OS"
                      />
                    </td>
                    <td>
                      <Situacao
                        t={t?.v2}
                        carregando={emTeste}
                        extra={
                          t?.v2.origem && (
                            <p className="text-secondary small mb-0">
                              {t.v2.origem === 'tela'
                                ? 'chave colada aqui'
                                : 'chave do servidor'}
                            </p>
                          )
                        }
                      />
                    </td>
                    <td className="text-end text-nowrap pe-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-theme"
                        onClick={() => {
                          setTrocandoChave(trocandoChave === l.loja_codigo ? null : l.loja_codigo);
                          setChave('');
                        }}
                      >
                        <i className="bi bi-key me-1"></i>Trocar chave
                      </button>
                    </td>
                  </tr>
                  {trocandoChave === l.loja_codigo && (
                    <tr>
                      <td colSpan={3} className="bg-body-tertiary px-3">
                        <div className="row g-2 align-items-center">
                          <div className="col">
                            <input
                              type="password"
                              className="form-control form-control-sm"
                              placeholder={`Cole aqui o token da API v2 de ${l.loja_nome_curto}`}
                              autoComplete="off"
                              data-form-type="other"
                              data-lpignore="true"
                              data-1p-ignore="true"
                              value={chave}
                              onChange={(e) => setChave(e.target.value)}
                            />
                            <div className="form-text">
                              O token da API v2 gerado no Tiny desta loja. Ele é testado
                              no Tiny antes de ser salvo — chave de outra loja é recusada.
                            </div>
                          </div>
                          <div className="col-auto align-self-start">
                            <button
                              type="button"
                              className="btn btn-sm btn-theme me-2"
                              onClick={() => salvarChave(l.loja_codigo)}
                              disabled={!chave.trim() || salvandoChave}
                            >
                              {salvandoChave && <span className="spinner-border spinner-border-sm me-1" />}
                              Testar e salvar
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-secondary"
                              onClick={() => setTrocandoChave(null)}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td className="ps-3">
                      <Identidade
                        icone="bi-tags"
                        cor="theme-purple"
                        nome="Categorias"
                        uso="A lista da aba Tiny da despesa"
                      />
                    </td>
                    <td>
                      <p className="mb-0">{cat ? `${cat.qtd} categorias` : '—'}</p>
                      <p className="text-secondary small mb-0">
                        {cat?.ultima_sincronizacao
                          ? `sincronizadas em ${formatarDataHora(cat.ultima_sincronizacao)}`
                          : 'nunca sincronizadas'}
                      </p>
                    </td>
                    <td className="text-end text-nowrap pe-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-theme"
                        onClick={() => sincronizar(l)}
                        disabled={sincronizando !== null}
                      >
                        {sincronizando === l.loja_codigo ? (
                          <span className="spinner-border spinner-border-sm me-1" />
                        ) : (
                          <i className="bi bi-arrow-repeat me-1"></i>
                        )}
                        Sincronizar
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {lojas.length === 0 && <p className="text-secondary small mb-0">Nenhuma loja ativa.</p>}

      <Modal
        className="casaa-modal"
        show={desconectando !== null}
        onHide={() => setDesconectando(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title as="h5">Desconectar {desconectando?.loja_nome_curto} do Tiny</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            A chave da API v3 desta loja é apagada. Enquanto ela não for conectada de
            novo, <strong>nenhuma despesa de {desconectando?.loja_nome_curto} vai
            para o Tiny</strong> e as categorias não se atualizam.
          </p>
          <p className="mb-0 text-secondary small">
            A API v2 (puxar pedido de venda) não é afetada. Para voltar, use
            "Copiar link" e autorize no navegador do Tiny desta loja.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setDesconectando(null)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => desconectando && desconectar(desconectando.loja_codigo)}
          >
            Desconectar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
